import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';

// Same safety-net principle as useSiteImage/useHeaderFooterLinks: a
// missing row, an empty table, or an unreachable DB must never blank a
// page — every section defaults to visible unless a row explicitly says
// otherwise. One shared table (rxk_page_sections, keyed by page_key +
// section_key) backs every storefront page's section on/off toggle, not
// just Home's.
const cache: Record<string, Record<string, boolean>> = {};
const inFlight: Record<string, Promise<Record<string, boolean>>> = {};

async function fetchVisibility(pageKey: string): Promise<Record<string, boolean>> {
  try {
    const { data } = await supabase.from('rxk_page_sections').select('section_key, is_visible').eq('page_key', pageKey);
    const map: Record<string, boolean> = {};
    for (const row of (data as { section_key: string; is_visible: boolean }[]) ?? []) map[row.section_key] = row.is_visible;
    cache[pageKey] = map;
    return map;
  } catch {
    return {};
  }
}

function loadVisibility(pageKey: string): Promise<Record<string, boolean>> {
  if (cache[pageKey]) return Promise.resolve(cache[pageKey]);
  if (!inFlight[pageKey]) inFlight[pageKey] = fetchVisibility(pageKey);
  return inFlight[pageKey];
}

export function usePageSections(pageKey: string): (sectionKey: string) => boolean {
  const [map, setMap] = useState<Record<string, boolean>>(cache[pageKey] ?? {});

  useEffect(() => {
    let mounted = true;
    loadVisibility(pageKey).then((m) => {
      if (mounted) setMap(m);
    });
    return () => {
      mounted = false;
    };
  }, [pageKey]);

  return (sectionKey: string) => map[sectionKey] !== false;
}
